import type { HTMLAttributes } from 'react'
import { useNavigate } from 'react-router'
import type { CalendarEvent, CalendarProps, CalendarResource, BookingStatus, PaymentStatus } from '@/lib/render'
import { attendanceState, hasArrived } from '@/lib/render'
import { cn } from '@/lib/utils'
import { useRole } from '@/hooks/useRole'
import {
  blocksForColumn,
  buildSlotBands,
  busyRangesFor,
  minutesFromLocalTime,
  minutesToLabel,
  UNASSIGNED_COLUMN_ID,
  type BusyRange,
  type MoveProposal,
  type SlotPick,
} from '@/lib/slotBooking'
import type { CalendarBlock, CalendarNote } from '@/lib/render'

export interface CalendarViewProps {
  data: CalendarProps
  /**
   * Fill the space the parent gives it: columns share the width (never under
   * their minimum) and the grid scrolls inside the parent's height. /schedule
   * only; a chat calendar keeps its fixed size. Only the day view honours it.
   */
  fill?: boolean
  /**
   * Turn appointment tiles into quick-action menus. OFF by default: the same
   * renderers draw customer-facing chat calendars, and a customer must never be
   * shown "mark paid". Only an operator surface opts in.
   */
  interactive?: boolean
  /** Refetch after a quick action mutates a booking. */
  onChanged?: () => void
  /**
   * Clicking empty time proposes a booking. Gated by the same operator check as
   * `interactive` — a customer looking at their own calendar must never be handed
   * a booking form over the whole business.
   */
  onPickSlot?: (pick: SlotPick) => void
  /**
   * Dragging an appointment proposes a new TIME for it. Gated by the same
   * operator check as `interactive` and `onPickSlot` — a customer must never be
   * able to move an appointment by dragging it, and a tile that moved and then
   * snapped back would be worse than one that never moved.
   *
   * A proposal is not a write: `CalendarRenderer` opens the move dialog with it.
   */
  onMoveBooking?: (proposal: MoveProposal) => void
  /**
   * Draw a column for EVERY machine and a full grid even on a day with no
   * bookings. Off by default so chat calendars stay compact: an idle machine in
   * a "how does Friday look" answer is noise. The /schedule screen turns it on,
   * because the machines with nothing on them are exactly the ones an operator
   * wants to book into, and without a column there is nothing to click.
   */
  showAllResources?: boolean
  /**
   * Leave out the date line above the grid. /schedule names the day in its own
   * toolbar; a chat calendar has nothing else saying which day it is.
   */
  hideDateHeading?: boolean
}

/**
 * Everything a tile needs to turn a vertical drag into a proposed start.
 *
 * Passed down from whichever day view drew the grid, because only the view knows
 * its own pixels-per-minute and window — the two day grids draw at 2.25 and 1.5
 * px/min respectively, so a hard-coded scale would land a drop on the wrong slot
 * in one of them.
 */
export interface TileMoveGeometry {
  pixelsPerMinute: number
  intervalMinutes: number
  dayStartMinutes: number
  dayEndMinutes: number
  /** The anchor day, so the proposal can name a full datetime. */
  date: string
  onMoveBooking: (proposal: MoveProposal) => void
}

export const STATUS_COLORS: Record<BookingStatus, string> = {
  booked: '#0ea5e9',
  completed: '#22c55e',
  cancelled: '#ef4444',
  did_not_show: '#f97316',
  rescheduled: '#a855f7',
}

export const PAYMENT_COLORS: Record<PaymentStatus, string> = {
  paid: '#22c55e',
  partial: '#eab308',
  unpaid: '#ef4444',
}

export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function getDatePart(iso: string): string {
  return iso.slice(0, 10)
}

export function toLocalDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function getWeekDays(anchorDate: string): Date[] {
  const d = new Date(`${anchorDate}T00:00:00`)
  const dow = d.getDay()
  const monday = new Date(d)
  monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1))
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(monday)
    dd.setDate(monday.getDate() + i)
    return dd
  })
}

export function minutesFromMidnight(iso: string): number {
  const d = new Date(iso)
  return d.getHours() * 60 + d.getMinutes()
}

export function durationMins(start: string, end: string): number {
  return Math.max(5, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000))
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
}

export function formatHourLabel(hour: number): string {
  return `${hour % 12 === 0 ? 12 : hour % 12}${hour < 12 ? 'AM' : 'PM'}`
}

// Label for a minutes-from-midnight offset. On-the-hour → "8AM"; otherwise "8:30".
export function formatMinutesLabel(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return m === 0 ? `${hour12}${h < 12 ? 'AM' : 'PM'}` : `${hour12}:${String(m).padStart(2, '0')}`
}

export function parseHour(value: string | undefined, fallback: number): number {
  if (!value) return fallback
  const [hour] = value.split(':').map(Number)
  return Number.isFinite(hour) ? hour : fallback
}

export function getHours(data: CalendarProps): number[] {
  const open = parseHour(data.business_hours?.open, 8)
  const close = parseHour(data.business_hours?.close, 20)
  return Array.from({ length: Math.max(1, close - open + 1) }, (_, i) => open + i)
}

export function getEventResourceIds(event: CalendarEvent): string[] {
  if (event.resource_ids?.length) return event.resource_ids
  if (event.resource_id) return [event.resource_id]
  return []
}

export function deriveResources(events: CalendarEvent[], resources?: CalendarResource[]): CalendarResource[] {
  if (resources?.length) return resources

  const byKey = new Map<string, CalendarResource>()
  for (const event of events) {
    const ids = getEventResourceIds(event)
    if (ids.length) {
      ids.forEach((id, index) => {
        if (!byKey.has(id)) byKey.set(id, { id, name: event.resource_name ?? `Resource ${index + 1}`, display_order: event.resource_order })
      })
    } else if (event.resource_name) {
      byKey.set(event.resource_name, { id: event.resource_name, name: event.resource_name, display_order: event.resource_order })
    }
  }

  return Array.from(byKey.values())
}

export function eventBelongsToResource(event: CalendarEvent, resource: CalendarResource): boolean {
  const ids = getEventResourceIds(event)
  if (ids.length) return ids.includes(resource.id)
  return event.resource_name === resource.name || event.resource_name === resource.id
}

export function eventMatchesAnyResource(event: CalendarEvent, resources: CalendarResource[]): boolean {
  return resources.some(resource => eventBelongsToResource(event, resource))
}

export function statusColor(event: CalendarEvent): string {
  return event.color ?? STATUS_COLORS[event.status] ?? '#94a3b8'
}

// Text prefix for the tile's meta line. Colour alone must never carry status —
// every non-booked state says its name on the tile, matching the treatment
// cancelled always had. Booked is the default and stays unlabelled.
const STATUS_PREFIX: Record<BookingStatus, string> = {
  booked: '',
  completed: 'Completed · ',
  cancelled: 'Cancelled · ',
  did_not_show: 'No-show · ',
  rescheduled: 'Rescheduled · ',
}

export function statusPrefix(event: CalendarEvent): string {
  return STATUS_PREFIX[event.status] ?? ''
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Click-through from a calendar tile to its booking record. event.id IS the
// booking id when the calendar was assembled from real bookings (lib/calendar.ts
// and v1-chat both map booking.id → event.id). Two guards:
//  - operator-only: customer chat can render calendars too, and /booking/:id is
//    an operator screen;
//  - UUID-shaped ids only: an AI-composed calendar may carry synthetic ids, and
//    a dead link is worse than no link.
export function useOpenBooking(): ((event: CalendarEvent) => void) | null {
  const navigate = useNavigate()
  const { userRole } = useRole()
  if (userRole !== 'owner' && userRole !== 'staff') return null
  return (event: CalendarEvent) => {
    if (UUID_RE.test(event.id)) navigate(`/booking/${event.id}`)
  }
}

/** Spread onto a tile div to make it act as a link when opening is allowed. */
export function tileLinkProps(
  event: CalendarEvent,
  openBooking: ((event: CalendarEvent) => void) | null,
): HTMLAttributes<HTMLDivElement> {
  if (!openBooking || !UUID_RE.test(event.id)) return {}
  return {
    role: 'link',
    tabIndex: 0,
    onClick: () => openBooking(event),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openBooking(event)
      }
    },
  }
}

// Payment at a glance: a plain '$' when the booking is fully paid, a struck-through
// '$' when money is still owed. Deliberately a glyph rather than the coloured dot
// this replaced — paid/unpaid has to be readable without relying on colour, and
// "completed but never paid" is exactly the signal the status lifecycle exists to
// surface. Partial keeps its own colour so it stays distinguishable from unpaid.
/**
 * Attendance at a glance: nothing before they arrive, a hollow tick while they
 * are IN SESSION, a filled one once the session is finished.
 *
 * Two marks where there used to be one, because "arrived" and "finished" became
 * different things on 2026-08-17. They are distinguished by SHAPE as well as by
 * shade, and each carries its own title and aria-label — a tick that only differs
 * in colour would say nothing to a screen reader and nothing in greyscale.
 *
 * Every historical `completed` booking has no timestamps and still renders the
 * finished mark, because `attendanceState` reads the status first.
 */
export function AttendanceMark({
  event,
}: {
  event: Pick<CalendarEvent, 'status' | 'checked_in_at' | 'checked_out_at'>
}) {
  if (!hasArrived(event)) return null
  const state = attendanceState(event)
  const inSession = state === 'in_session'
  const label = inSession ? 'In session' : 'Checked out'
  return (
    <div
      className={cn('rounded-md px-1', inSession ? 'bg-sky-100 dark:bg-sky-950' : 'bg-green-100 dark:bg-green-950')}
      title={label}
    >
      <span
        className={cn(
          'text-[10px] font-extrabold',
          inSession ? 'text-sky-700 dark:text-sky-300' : 'text-green-600 dark:text-green-400',
        )}
        aria-label={label}
      >
        {inSession ? '◦' : '✓'}
      </span>
    </div>
  )
}

/**
 * The tile line's readiness dot — 🟢 credit in order (or paid), 🟠 credit fine
 * but intake incomplete (Phase 4; the state is wired, no producer sets it yet),
 * 🔴 nothing can cover the session.
 *
 * ADDITIVE to the lifecycle marks: the left border colour and statusPrefix keep
 * carrying booked/completed/cancelled/did_not_show — this dot only ever appears
 * on a `booked` tile (tileReadiness returns null otherwise). Colour never
 * carries it alone: each state has its own SHAPE (filled disc / ring / cross)
 * plus a title and aria-label from readiness_label.
 */
export function ReadinessDot({ readiness, label }: { readiness: 'ready' | 'attention' | 'blocked'; label: string }) {
  const cls =
    readiness === 'ready' ? 'text-green-600 dark:text-green-400'
    : readiness === 'attention' ? 'text-amber-600 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400'
  const glyph = readiness === 'ready' ? '●' : readiness === 'attention' ? '◯' : '✕'
  return (
    <span className={cn('shrink-0 text-[9px] leading-none', cls)} title={label} aria-label={label}>
      {glyph}
    </span>
  )
}

export function PaymentMark({ status, byPackage }: { status: PaymentStatus; byPackage?: boolean }) {
  const owing = status !== 'paid'
  const label = status === 'paid'
    ? (byPackage ? 'Paid by package credit' : 'Paid')
    : status === 'partial' ? 'Partly paid' : 'Not paid'
  return (
    <span
      className={cn('text-xs font-bold leading-[14px]', owing && 'line-through')}
      style={{ color: PAYMENT_COLORS[status] }}
      aria-label={label}
      title={label}
    >
      $
    </span>
  )
}

export function EventCard({ event, compact, linkProps }: {
  event: CalendarEvent
  compact?: boolean
  linkProps?: HTMLAttributes<HTMLDivElement>
}) {
  const color = statusColor(event)
  const cancelled = event.status === 'cancelled'
  const clickable = Boolean(linkProps?.onClick)

  if (compact) {
    return (
      <div
        {...linkProps}
        className={cn('rounded-[4px] border-l-[3px] bg-muted px-[5px] py-1', cancelled && 'opacity-60', clickable && 'cursor-pointer hover:bg-accent')}
        style={{ borderLeftColor: color }}
      >
        <p className={cn('truncate text-[10px] font-bold text-foreground', cancelled && 'line-through')}>{event.service_name}</p>
        <div className="flex items-center justify-between gap-1">
          <p className={cn('mt-px min-w-0 truncate text-[9px] text-muted-foreground', cancelled && 'line-through')}>
            {formatTime(event.start)}{event.customer_name ? ` · ${event.customer_name}` : ''}
          </p>
          {event.payment_status ? <PaymentMark status={event.payment_status} byPackage={event.paid_with_package} /> : null}
        </div>
      </div>
    )
  }

  return (
    <div
      {...linkProps}
      className={cn('min-h-11 overflow-hidden rounded-sm border-l-4 px-1.5 py-[5px]', cancelled && 'opacity-60', clickable && 'cursor-pointer hover:brightness-95')}
      style={{ borderLeftColor: color, backgroundColor: `${color}22` }}
    >
      <div className="flex items-center justify-between gap-1.5">
        <p className={cn('min-w-0 flex-1 truncate text-xs font-bold', cancelled && 'line-through')} style={{ color }}>{event.service_name}</p>
        <div className="flex items-center gap-[5px]">
          <AttendanceMark event={event} />
          {event.payment_status ? <PaymentMark status={event.payment_status} byPackage={event.paid_with_package} /> : null}
        </div>
      </div>
      {event.customer_name ? (
        // The tile line: 🟢 Sara Walling · 1/6 🎂. Dot, credit counts and cake
        // are all optional enrichment (lib/daySheet.ts) — an AI chat calendar's
        // events never carry them, and the line degrades to the bare name.
        <p className={cn('mt-0.5 flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground', cancelled && 'line-through')}>
          {event.readiness && event.readiness_label ? (
            <ReadinessDot readiness={event.readiness} label={event.readiness_label} />
          ) : null}
          <span className="min-w-0 truncate">
            {event.customer_name}
            {event.credit_label ? ` · ${event.credit_label}` : ''}
          </span>
          {event.birthday_label ? (
            <span className="shrink-0" title={event.birthday_label} aria-label={event.birthday_label}>
              🎂
            </span>
          ) : null}
        </p>
      ) : null}
      <p className={cn('mt-0.5 truncate text-[10px] text-muted-foreground', cancelled && 'line-through')}>
        {statusPrefix(event)}{formatTime(event.start)} · {durationMins(event.start, event.end)}min
      </p>
    </div>
  )
}

/**
 * The clickable empty time inside ONE machine column.
 *
 * One button per slot-interval band rather than one hit area plus pixel maths:
 * each band already knows its own time, so there is no offsetY→minutes
 * conversion to get wrong, and every band gets a real accessible name so the
 * grid is reachable by keyboard as well as by mouse.
 *
 * Render this BEFORE the event cards in the column. Both are absolutely
 * positioned with no z-index, so paint order is DOM order — the cards land on
 * top and a click on a booking opens its quick actions, never the booking
 * dialog. That is why there is no stopPropagation anywhere here.
 *
 * A band is a PROPOSAL. It is filtered against bookings only; business hours,
 * resource schedules, time blocks and buffers are checked by the dialog against
 * `GET /v1-availability`. See lib/slotBooking.ts.
 */
export function SlotBandLayer({
  date,
  resource,
  events,
  blocked,
  dayStartMinutes,
  dayEndMinutes,
  intervalMinutes,
  pixelsPerMinute,
  onPickSlot,
}: {
  date: string
  resource: CalendarResource
  events: CalendarEvent[]
  /** Closures already narrowed to this column and clipped to the day. */
  blocked?: BusyRange[]
  dayStartMinutes: number
  dayEndMinutes: number
  intervalMinutes: number
  pixelsPerMinute: number
  onPickSlot: (pick: SlotPick) => void
}) {
  // A RETIRED machine keeps its column (a booking in view still needs its name)
  // but is never bookable: it produces no availability slots and is never
  // auto-selected, so a band on it could only ever fail. Enforced here rather
  // than in each view so neither can forget it. Absent means active.
  if (resource.is_active === false) return null

  const bands = buildSlotBands({
    dayStartMinutes,
    dayEndMinutes,
    intervalMinutes,
    busy: busyRangesFor(events),
    blocked,
  })
  const isUnassigned = resource.id === UNASSIGNED_COLUMN_ID
  const resourceId = isUnassigned ? null : resource.id
  const resourceName = isUnassigned ? null : resource.name

  return (
    <>
      {bands.map(band => (
        <button
          key={band.startMinutes}
          type="button"
          onClick={() => onPickSlot({ date, startMinutes: band.startMinutes, resourceId, resourceName })}
          aria-label={`Book ${minutesToLabel(band.startMinutes)}${resourceName ? ` on ${resourceName}` : ''}`}
          title={`Book ${minutesToLabel(band.startMinutes)}`}
          className="group absolute inset-x-1 flex cursor-pointer items-center justify-center rounded-sm border border-transparent hover:border-primary/40 hover:bg-primary/5 focus-visible:border-primary focus-visible:outline-none"
          style={{
            top: (band.startMinutes - dayStartMinutes) * pixelsPerMinute,
            height: Math.max(1, (band.endMinutes - band.startMinutes) * pixelsPerMinute - 1),
          }}
        >
          <span className="truncate px-1 text-[10.5px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            + {minutesToLabel(band.startMinutes)}
          </span>
        </button>
      ))}
    </>
  )
}

/**
 * Closed time in ONE column, drawn UNDER everything else.
 *
 * Hatched rather than a flat fill so it reads as "not available" instead of as a
 * strangely coloured booking, and `pointer-events-none` so it can never
 * intercept a click meant for a tile — the closure is information, not a
 * control. Lifting a block is done from the Time blocks screen or from chat,
 * which is also where the confirmation lives; there is no undo hidden on the
 * grid.
 *
 * Ranges arrive already narrowed to this column and clipped to the day (see
 * blocksForColumn), which is what makes a business-wide closure appear on every
 * column and a week-long one stop at midnight.
 */
export function ClosedTimeLayer({
  ranges,
  labels,
  dayStartMinutes,
  pixelsPerMinute,
}: {
  ranges: BusyRange[]
  /** Reason per range, same order. Drawn only when the band is tall enough to read. */
  labels?: (string | null)[]
  dayStartMinutes: number
  pixelsPerMinute: number
}) {
  return (
    <>
      {ranges.map((range, index) => {
        const height = Math.max(2, (range.endMinutes - range.startMinutes) * pixelsPerMinute)
        const reason = labels?.[index] ?? null
        return (
          <div
            key={`${range.startMinutes}-${range.endMinutes}-${index}`}
            className="pointer-events-none absolute inset-x-0 overflow-hidden border-y border-border/60 bg-muted/80"
            style={{
              top: (range.startMinutes - dayStartMinutes) * pixelsPerMinute,
              height,
              // A diagonal hatch, so "closed" survives a greyscale print and does
              // not rely on the fill colour alone.
              backgroundImage:
                'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(100,116,139,0.22) 5px, rgba(100,116,139,0.22) 10px)',
            }}
            role="note"
            aria-label={`Closed${reason ? `: ${reason}` : ''}`}
          >
            {height >= 22 ? (
              <span className="block truncate px-1.5 pt-0.5 text-[10.5px] font-semibold text-muted-foreground">
                Closed{reason ? ` · ${reason}` : ''}
              </span>
            ) : null}
          </div>
        )
      })}
    </>
  )
}

/**
 * Memos in ONE column. Drawn ABOVE the slot bands but with pointer events off,
 * so the time underneath a memo stays clickable — which is the whole point of a
 * memo not being a closure. A chip that swallowed the click would quietly make
 * the slot unbookable through the UI while the API still sold it.
 */
export function CalendarNoteLayer({
  notes,
  dayStartMinutes,
  pixelsPerMinute,
}: {
  notes: CalendarNote[]
  dayStartMinutes: number
  pixelsPerMinute: number
}) {
  return (
    <>
      {notes.map((note) => (
        <div
          key={note.id}
          className="pointer-events-none absolute inset-x-1 z-20 flex items-start gap-1 rounded-sm border-l-2 border-amber-400 bg-amber-50/95 px-1.5 py-0.5 dark:bg-amber-950/80"
          style={{ top: (minutesFromLocalTime(note.at) - dayStartMinutes) * pixelsPerMinute }}
          title={note.note}
        >
          <span className="truncate text-[10.5px] font-medium text-amber-900 dark:text-amber-100">
            {note.note}
          </span>
        </div>
      ))}
    </>
  )
}

/**
 * The blocks and memos that belong to one column, ready to draw.
 *
 * A note with no `resource_id` belongs to the day, and the DAY is represented by
 * the first column — repeating it in every machine lane would say the same thing
 * five times across one row. A business-wide CLOSURE is the opposite: it really
 * does close every column, and blocksForColumn already expands it.
 */
export function columnOverlays(
  data: CalendarProps,
  resource: CalendarResource,
  isFirstColumn: boolean,
): { blocked: BusyRange[]; blockLabels: (string | null)[]; notes: CalendarNote[] } {
  const isUnassigned = resource.id === UNASSIGNED_COLUMN_ID
  const resourceId = isUnassigned ? null : resource.id

  const allBlocks: CalendarBlock[] = data.blocks ?? []
  const asTimeBlocks = allBlocks.map(b => ({
    id: b.id,
    resource_id: b.resource_id,
    block_start: b.start,
    block_end: b.end,
    reason: b.reason ?? null,
  }))
  const blocked = blocksForColumn(asTimeBlocks, resourceId, data.date)
  // Re-derive the reason for each surviving range by matching on start minute;
  // blocksForColumn returns bare ranges so it can stay pure and testable.
  const blockLabels = blocked.map((range) => {
    const match = allBlocks.find(
      b =>
        (b.resource_id === null || b.resource_id === resourceId) &&
        (minutesFromLocalTime(b.start) === range.startMinutes || range.startMinutes === 0),
    )
    return match?.reason ?? null
  })

  const notes = (data.notes ?? []).filter((note) => {
    if (note.at.slice(0, 10) !== data.date) return false
    if (note.resource_id === null) return isFirstColumn
    return note.resource_id === resourceId
  })

  return { blocked, blockLabels, notes }
}

export function EmptyState({ label = 'No appointments' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center py-6">
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  )
}

export function StatusLegend() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {(Object.entries(STATUS_COLORS) as [BookingStatus, string][]).map(([status, color]) => (
        <div key={status} className="flex items-center gap-1">
          <div className="h-[7px] w-[7px] rounded-full" style={{ backgroundColor: color }} />
          <span className="text-[10px] capitalize text-muted-foreground">{status.replace(/_/g, ' ')}</span>
        </div>
      ))}
      <div className="h-3 w-px bg-border" />
      {(Object.keys(PAYMENT_COLORS) as PaymentStatus[]).map(status => (
        <div key={status} className="flex items-center gap-1">
          <PaymentMark status={status} />
          <span className="text-[10px] capitalize text-muted-foreground">{status}</span>
        </div>
      ))}
    </div>
  )
}
